import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, IconButton, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { IUserReadResourceV12, OrderScenario } from 'components/order/model/Order';
import { getTimeslotTimes } from 'components/timeslots/helpers';
import { addOpacity } from 'lib/helpers';
import { UserAvatar } from 'lib/UserAvatar';
import { ApplicationState } from 'store/store';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import moment from 'moment';

const PREFIX = 'ViewBillHeaderInfo';

const classes = {
    title: `${PREFIX}title`,
    userInitials: `${PREFIX}userInitials`,
    users: `${PREFIX}users`,
    hint: `${PREFIX}hint`,
    icon: `${PREFIX}icon`,
    textBold: `${PREFIX}textBold`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.title}`]: {
        textAlign: 'center',
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary
    },

    [`& .${classes.userInitials}`]: {
        fontSize: theme.spacing(2),
        color: theme.palette.common.black,
        border: `1px solid ${addOpacity(theme.palette.common.black, 0.6)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        width: '48px',
        height: '48px',
        marginRight: theme.spacing(0.5),
        flexShrink: 0
    },

    [`& .${classes.users}`]: {
        display: 'flex',
        paddingLeft: theme.spacing(2),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
            display: 'none'
        }
    },

    [`& .${classes.hint}`]: {
        color: theme.palette.hint
    },

    [`& .${classes.icon}`]: {
        color: theme.palette.hint
    },

    [`& .${classes.textBold}`]: {
        fontWeight: 700
    }
}));

interface IProps {
    users?: IUserReadResourceV12[];
    pin?: string;
    passphrase?: string;
    infoVisible?: boolean;
    locationVisible?: boolean;
    address?: string;
    dropoff?: Date;
    dropoffDeadline?: Date;
}

export const ViewBillHeaderInfo: React.FC<React.PropsWithChildren<IProps>> = ({
    users,
    pin,
    passphrase,
    locationVisible = true,
    infoVisible = false,
    address,
    dropoff,
    dropoffDeadline
}) => {
    const { t } = useTranslation();

    const [isHeaderVisible, setIsHeaderVisible] = React.useState(true);
    const [scenario] = useRouteScenario();

    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);
    const { deliveryLocation, timeSlot, prepTimePadding } = useSelector(
        (state: ApplicationState) => state.basket
    );
    const scenarioToUse = React.useMemo(() => scenario ?? OrderScenario.PREORDER, [scenario]);
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    const joinedUsers = React.useMemo(() => (users ? users.filter(user => !user.hasLeft) : []), [users]);
    const timePadding = React.useMemo(() => prepTimePadding ?? 0, [prepTimePadding]);

    const renderUserInitials = React.useCallback(
        (user: IUserReadResourceV12) => <UserAvatar user={user} key={user.id} />,
        []
    );
    const handleInfoClick = React.useCallback(() => setIsHeaderVisible(!isHeaderVisible), [isHeaderVisible]);
    const subtitle = React.useMemo(() => {
        switch (scenarioToUse) {
            case OrderScenario.TABLE:
                return (
                    deliveryLocation && (
                        <Typography
                            align="center"
                            variant="body2"
                            className={classes.textBold}
                            data-cy="table-number"
                        >
                            {t('BILL_TABLE_NUMBER', { tableNumber: deliveryLocation })}
                        </Typography>
                    )
                );

            case OrderScenario.PREORDER:
                return timeSlot ? (
                    <Typography align="center" variant="body2" className={classes.textBold}>
                        {getTimeslotTimes(timeSlot, t, true)}
                    </Typography>
                ) : (
                    <Typography align="center" variant="body2" className={classes.textBold}>
                        {t('ORDER_STATUS_ORDER_READY_IN_MINS', {
                            from: (settings?.preOrder.readyPeriodStart || 5) + timePadding,
                            to: (settings?.preOrder.readyPeriodEnd || 20) + timePadding
                        })}
                    </Typography>
                );
            case OrderScenario.ORDER_TO_TABLE:
                return (
                    <Typography align="center" variant="body2" className={classes.textBold}>
                        {t('LABEL_TABLE_NUMBER_PLACEHOLDER', { tableNumber: deliveryLocation })}
                    </Typography>
                );
            case OrderScenario.DELIVERY:
                return (
                    <Box>
                        <Typography align="center" variant="body2" className={classes.textBold}>
                            {address}
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                            <AccessTimeIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                            <Typography align="center" variant="body2">
                                {t('DELIVERY_ETA_TIMESLOT_MESSAGE', {
                                    timeslot: `${moment(dropoff).format('HH:mm')} - ${moment(
                                        dropoffDeadline
                                    ).format('HH:mm')}`
                                })}
                            </Typography>
                        </Box>
                    </Box>
                );
            default:
        }
    }, [
        scenarioToUse,
        deliveryLocation,
        t,
        timeSlot,
        settings?.preOrder.readyPeriodStart,
        settings?.preOrder.readyPeriodEnd,
        timePadding,
        address,
        dropoff,
        dropoffDeadline
    ]);

    return (
        <StyledBox>
            <Box display="flex" alignItems="center" justifyContent="center">
                {locationVisible && (
                    <Typography className={classes.title} variant="h6">
                        {currentLocation?.title}
                    </Typography>
                )}
                {infoVisible && joinedUsers.length > 1 && (
                    <IconButton size="small" onClick={handleInfoClick}>
                        <InfoIcon className={classes.icon} />
                    </IconButton>
                )}
            </Box>
            {subtitle}
            {users && joinedUsers.length > 1 && isHeaderVisible && (
                <Box className={classes.users} paddingLeft={2}>
                    {joinedUsers.map(renderUserInitials)}
                </Box>
            )}
            {currentLocation && isHeaderVisible && !currentLocation.services?.pay_at_table && (
                <>
                    {pin && (
                        <Typography align="center">
                            {t('VIEW_BILL_INVITE_PIN_LABEL')}
                            <span className={classes.hint}>{pin}</span>
                        </Typography>
                    )}
                    {passphrase && (
                        <Typography align="center">
                            {t('VIEW_BILL_PASSPHRASE_LABEL')}
                            <span className={classes.hint}>{passphrase}</span>
                        </Typography>
                    )}
                </>
            )}
        </StyledBox>
    );
};
